import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  SparklesIcon, 
  CpuChipIcon, 
  HeartIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const LandingPage = () => {
  const features = [
    {
      icon: SparklesIcon,
      title: 'AI-Powered Planning',
      description: 'Get personalized meal plans generated by advanced AI based on your ingredients and preferences.'
    },
    {
      icon: CpuChipIcon,
      title: 'Smart Recipe Matching',
      description: 'Input your available ingredients and discover perfect recipes that maximize what you have.'
    },
    {
      icon: HeartIcon,
      title: 'Dietary Preferences',
      description: 'Support for vegan, vegetarian, keto, gluten-free, and many more dietary requirements.'
    },
    {
      icon: ChartBarIcon,
      title: 'Nutrition Tracking',
      description: 'Monitor your nutritional intake with detailed breakdowns and goal tracking.'
    }
  ];

  return (
    <div className="overflow-hidden bg-cream">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-b from-white to-orange-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center space-y-6 max-w-3xl mx-auto"
          >
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-orange-50 border border-orange-200 rounded-full px-4 py-2">
              <SparklesIcon className="w-5 h-5 text-orange-600" />
              <span className="text-orange-600 text-sm font-medium">
                AI-Powered Meal Planning
              </span>
            </div>

            {/* Main heading */}
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                <span className="text-orange-500">Smart Meal Planning</span>
                <br />
                <span className="text-sage-600">Powered by AI</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
                Transform your available ingredients into personalized meal plans with 
                AI-powered recipe suggestions and nutrition tracking.
              </p>
            </div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center pt-6"
            >
              <Link 
                to="/register" 
                className="inline-flex items-center justify-center px-8 py-4 text-base font-medium rounded-lg bg-orange-500 text-white hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 shadow-lg hover:shadow-xl border border-transparent transition-all duration-300"
              >
                Start Planning Free
              </Link>
              <Link 
                to="/recipes" 
                className="inline-flex items-center justify-center px-8 py-4 text-base font-medium rounded-lg bg-white text-orange-600 hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 border border-orange-200 transition-all duration-300"
              >
                Browse Recipes
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
              <span className="text-orange-500">Everything you need</span> <span className="text-sage-600">for smart meal planning</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Discover how AI can revolutionize your approach to nutrition and meal preparation
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="rounded-lg border text-card-foreground flex flex-col items-center text-center p-8 bg-white border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-center mb-6 p-4 bg-orange-50 rounded-full group-hover:bg-orange-100 transition-colors duration-300">
                    <feature.icon className="w-10 h-10 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-4 text-sage-600 group-hover:text-orange-600 transition-colors duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 lg:py-32 bg-gradient-to-b from-white to-orange-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl p-8 md:p-12 text-center space-y-6 shadow-xl border border-gray-100"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-sage-600">
              Ready to transform your meal planning?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Join thousands of users who have revolutionized their approach to nutrition with AI-powered meal planning.
            </p>
            <Link 
              to="/register" 
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium rounded-lg bg-orange-500 text-white hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Get Started Today
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;